import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { TooltipProvider } from '@/components/ui/tooltip'
import { AuthGate } from '@/components/auth/AuthGate'
import { configureApiClient } from '@/api/client'
import { queryKeys } from '@/api/queryKeys'
import { authHeaders } from '@/lib/authStorage'
import { initNative } from '@/native'
import { simulateNotchInDev } from '@/lib/devNotch'
import { lockZoomOnTouch } from '@/lib/lockZoom'
import App from './App'
// Self-hosted (bundled into the web build and the iOS app): no request to Google, works offline.
import '@fontsource/gabriela/400.css'
import '@fontsource/lato/100.css'
import '@fontsource/lato/100-italic.css'
import '@fontsource/lato/300.css'
import '@fontsource/lato/300-italic.css'
import '@fontsource/lato/400.css'
import '@fontsource/lato/400-italic.css'
import '@fontsource/lato/700.css'
import '@fontsource/lato/700-italic.css'
import '@fontsource/lato/900.css'
import '@fontsource/lato/900-italic.css'
import './index.css'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 30,
    },
  },
})

initNative()
lockZoomOnTouch()
simulateNotchInDev()

configureApiClient({
  getAuthHeaders: authHeaders,
  // A 401 anywhere means the session expired or was revoked: recheck, and the gate shows sign-in.
  onUnauthorized: () => void queryClient.invalidateQueries({ queryKey: queryKeys.session() }),
})

function render() {
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider delayDuration={400}>
          <AuthGate>
            <App />
          </AuthGate>
        </TooltipProvider>
      </QueryClientProvider>
    </StrictMode>,
  )
}

// Dev-only /newuser: sign in as a fresh preview account first (stripped from builds).
if (import.meta.env.DEV && window.location.pathname === '/newuser') {
  void import('@/dev/newUserPreview').then((m) => m.startNewUserPreview()).finally(render)
} else {
  render()
}
